import {
  type AnnouncementKey,
  type AnnouncementOutcome,
  getPendingAnnouncementsOptions,
  getPendingAnnouncementsQueryKey,
  markAnnouncementSeenMutation,
  type PendingAnnouncement,
} from "@sunner-cloud/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import type { announcementRegistry } from "./announcementRegistry";

import {
  trackAnnouncementCtaClicked,
  trackAnnouncementDismissed,
  trackAnnouncementShown,
} from "@/config/mixpanel";
import { useAuth } from "@/providers/auth";

interface Props {
  registry: typeof announcementRegistry;
}

const firstRegisteredKey = (
  registry: Props["registry"],
  pending: PendingAnnouncement[] | undefined,
) => pending?.find(({ key }) => Object.hasOwn(registry, key))?.key ?? null;

const TrackShown = ({
  announcementKey,
  children,
}: {
  announcementKey: AnnouncementKey;
  children: ReactNode;
}) => {
  useEffect(() => {
    trackAnnouncementShown(announcementKey);
  }, [announcementKey]);

  return children;
};

export const Announcements = ({ registry }: Props) => {
  const { isAuthenticated } = useAuth();
  const isOnLogin = useRouterState({
    select: (state) => state.location.pathname.startsWith("/login"),
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pending = useQuery({
    ...getPendingAnnouncementsOptions(),
    enabled: isAuthenticated,
  });
  const markSeen = useMutation(markAnnouncementSeenMutation());
  const [shownKey, setShownKey] = useState<AnnouncementKey | null>();

  if (!isAuthenticated && shownKey !== undefined) {
    setShownKey(undefined);
  }

  if (
    shownKey === undefined &&
    isAuthenticated &&
    !isOnLogin &&
    !pending.isFetching &&
    (pending.isSuccess || pending.isError)
  ) {
    setShownKey(
      pending.isSuccess ? firstRegisteredKey(registry, pending.data) : null,
    );
  }

  if (!shownKey) {
    return null;
  }

  const { Dialog, ctaNavigation } = registry[shownKey];

  const close = (outcome: AnnouncementOutcome) => {
    setShownKey(null);
    queryClient.setQueryData<PendingAnnouncement[]>(
      getPendingAnnouncementsQueryKey(),
      (current) => current?.filter(({ key }) => key !== shownKey),
    );
    markSeen.mutate({ path: { key: shownKey }, body: { outcome } });
    if (outcome === "cta") {
      trackAnnouncementCtaClicked(shownKey);
      void navigate(ctaNavigation);
    } else {
      trackAnnouncementDismissed(shownKey);
    }
  };

  return (
    <TrackShown announcementKey={shownKey}>
      <Dialog
        open
        onActivate={() => close("cta")}
        onDismiss={() => close("dismissed")}
      />
    </TrackShown>
  );
};
